<script>
    import { run, stopPropagation, createBubbler, self } from "svelte/legacy";

    const bubble = createBubbler();
    /**
     * based on
     * - https://github.com/PaulMaly/svelte-inline-edit
     * - https://github.com/ukchukx/svelte-inline-input
     */
    import { tick, createEventDispatcher } from "svelte";
    import { format } from "svelte-i18n";

    const dispatch = createEventDispatcher();

    /**
     * @typedef {Object} Props
     * @property {string} [type] - Props
     * @property {string} [value]
     * @property {string} [placeholder]
     * @property {string} [labelClasses]
     * @property {string} [inputClasses]
     * @property {number} [rows]
     * @property {number} [cols]
     * @property {any} [options]
     * @property {any} [attrs]
     * @property {any} [lang]
     */

    /** @type {Props} */
    let {
        type = "text",
        value = $bindable(""),
        placeholder = "",
        labelClasses = "",
        inputClasses = "",
        rows = 1,
        cols = 20,
        options = [],
        attrs = [],
        lang = {
            edit: $format("buttons.edit"),
            save: $format("buttons.save"),
            cancel: $format("buttons.cancel"),
        },
    } = $props();

    let valueOld = value;
    let showEditBtn = false;
    let editing = $state(false);
    let label = $state();
    let inputEl = $state();
    let selectedIndex = $state();

    // Computed
    let isText = $derived(type === "text");
    let isNumber = $derived(type === "number");
    let isTextArea = $derived(type === "textarea");
    let isSelect = $derived(type === "select");
    run(() => {
        if (!isSelect) {
            label = value;
        } else {
            selectedIndex = options.findIndex((o) => o.value === value);
            label = selectedIndex !== -1 ? options[selectedIndex].label : "";
        }
    });

    const edit = async () => {
        editing = true;
        await tick();
        inputEl.focus();
    };

    const handleInput = (e) => {
        value = isNumber ? +e.target.value : e.target.value;
    };

    const handleKeyUp = (e) => {
        // enter
        if (e.keyCode === 13 && !isTextArea) {
            save();
        }
        // escape
        else if (e.keyCode === 27) {
            cancel();
        }
    };

    const save = () => {
        valueOld = value;
        editing = false;
        dispatch("save", inputEl);
    };

    const cancel = () => {
        if (editing) {
            value = valueOld;
            editing = false;
            dispatch("cancel", inputEl);
        }
    };

    const handleBlur = () => {
        if (type !== "select") {
            cancel();
        }
    };
</script>

<!-- input:on:blur triggers before button:on:click -->
<svelte:body onclick={handleBlur} />

{#if !editing}
    <!-- @todo: on:focus doesnt work for the first click on span -->
    <div class={labelClasses + "inline-edit-container t-inline-edit"}>
        {#if label}
            <span class="inline-edit-label" onclick={stopPropagation(edit)}>
                {label}
            </span>
        {:else}
            <span
                class="inline-edit-placeholder"
                title={lang.edit}
                onclick={stopPropagation(edit)}
            >
                {placeholder}
            </span>
        {/if}
    </div>
{:else}
    {#if isText || isNumber}
        <input
            class={inputClasses + "t-input-inline-edit"}
            bind:this={inputEl}
            {type}
            {value}
            {placeholder}
            {...attrs}
            onkeyup={handleKeyUp}
            oninput={handleInput}
            onclick={self(stopPropagation(bubble("click")))}
        />
    {:else if isTextArea}
        <textarea
            class={inputClasses}
            bind:this={inputEl}
            bind:value
            {placeholder}
            {rows}
            {cols}
            {...attrs}
            onkeyup={handleKeyUp}
            onclick={self(stopPropagation(bubble("click")))}
        ></textarea>
    {:else}
        <select
            class={inputClasses}
            bind:this={inputEl}
            bind:value
            onkeyup={handleKeyUp}
        >
            {#if !value && placeholder}
                <option selected value disabled>{placeholder}</option>
            {/if}

            {#each options as { label, value }, i}
                <option key={i} {value}>
                    {label}
                </option>
            {/each}
        </select>
    {/if}
    <span>
        <button
            class="btn btn-xs btn-success mt-1 t-confirm-desc"
            title={lang.save}
            onclick={stopPropagation(save)}
        >
            <i class="fas fa-check"></i>
        </button>
        <button
            class="btn btn-xs btn-danger mt-1 t-cancel-desc"
            title={lang.cancel}
            onclick={stopPropagation(cancel)}
        >
            <i class="fas fa-times"></i>
        </button>
    </span>
{/if}

<style>
    div.inline-edit-container {
        font-style: italic;
        display: inline-block;
    }
    .inline-edit-placeholder {
        color: rgba(0, 0, 0, 0.5);
    }
    .inline-edit-label {
        white-space: pre-wrap;
    }
    .btn.btn-xs.btn-success i,
    .btn.btn-xs.btn-danger i {
        color: white !important;
    }
</style>
